import React, { useEffect, useState } from 'react';
import { Button, Tab, Tabs, Tooltip } from '@mui/material';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormProvider, useForm } from 'react-hook-form';
import {
  RewardAttributes,
  RewardPeriod,
  RewardType,
  rewardSchemaForLanguages,
} from '@loyalty/shared-common/rewards-api-client';
import { colorPalette } from '@qualifio/ui-commons';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faWrench } from '@fortawesome/pro-solid-svg-icons/faWrench';
import { makeStyles } from '@mui/styles';
import { useLeaverPrompt } from '@qualifio/ui-commons/dist/components/qlf-prompt';
import { useTranslation } from 'react-i18next';
import { CampaignAccessDetails } from './CampaignDetails';
import { CampaignCodeDetails } from './CampaignCodeDetails';
import { DetailForm } from '../../../shared/layout/DetailForm';
import { PromocodeImportList } from './PromocodeImportList';
import { RewardEdition } from '../panels';
import { engageIcon, promoCodeIcon } from '../../../shared/icons';
import { useFeatures } from '../../../features/FeaturesContext';
import { useJoiResolver } from '../../../shared/forms/use-joi-resolver';
import { useProgram } from '../../../loyalty/ProgramContext';

const useStyles = makeStyles(() => ({
  actionBar: {
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    background: colorPalette.white.main,
  },
}));

export function RewardsDetailsForm({
  reward,
  onSubmit,
  isLoading,
}: {
  reward: RewardAttributes;
  onSubmit: (values: RewardAttributes) => void;
  isLoading: boolean;
}): React.ReactElement {
  const classes = useStyles();
  const { t } = useTranslation();
  const { program } = useProgram();
  const { features } = useFeatures();
  const { write: rewardWrite } = features[Feature.REWARD];
  const { read: promocodeRead } = features[Feature.PROMOCODE];
  const isCreating = !reward.createdAt;

  const [tab, setTab] = useState('configuration');

  const resolver = useJoiResolver(rewardSchemaForLanguages(program.languages, isCreating));
  const form = useForm<RewardAttributes>({
    mode: 'onChange',
    defaultValues: reward,
    resolver,
  });

  const {
    reset,
    handleSubmit,
    formState: { isDirty, errors },
    watch,
  } = form;

  const handleTabChange = (_: unknown, value: string) => {
    setTab(value);
  };

  useEffect(() => {
    const formValue: RewardAttributes = { ...reward };

    switch (reward?.type) {
      case RewardType.CAMPAIGN_ACCESS:
        if (!reward.data) {
          formValue.data = {
            channelInfoByLanguage: undefined,
            accessData: {
              validityPeriod: { unit: RewardPeriod.HOUR, value: undefined as any },
              nbParticipations: undefined as any,
            },
          };
        }
        break;
      case RewardType.CAMPAIGN_CODE:
        if (!reward.data) {
          formValue.data = { campaignCodeData: { codeListId: undefined as any } };
        }
        break;
      case RewardType.PROMOCODE:
        formValue.data = undefined;
        break;
      default:
        break;
    }

    reset(formValue);
  }, [reward]);

  useLeaverPrompt(isDirty || isLoading);

  const rewardType = watch('type');

  return (
    <DetailForm>
      <div className={classes.actionBar}>
        <Tabs value={tab} onChange={handleTabChange}>
          <Tab
            value="configuration"
            icon={<FontAwesomeIcon icon={faWrench} />}
            label={
              <>
                {t('rewards.tabs.configuration')}
                {!!errors.i18nAttributes && (
                  <FontAwesomeIcon color="red" icon={faExclamationCircle} />
                )}
              </>
            }
          />
          {isCreating ? (
            <></>
          ) : rewardType === RewardType.CAMPAIGN_ACCESS ? (
            <Tab
              value="campaign"
              style={{ pointerEvents: 'auto' }}
              label={
                <Tooltip title={promocodeRead ? '' : t('shared.action.permission.required')}>
                  <div>
                    {t('rewards.tabs.campaign')}
                    {!!errors.data && <FontAwesomeIcon color="red" icon={faExclamationCircle} />}
                  </div>
                </Tooltip>
              }
              icon={<FontAwesomeIcon icon={engageIcon} />}
              disabled={isCreating || !promocodeRead}
            />
          ) : rewardType === RewardType.PROMOCODE ? (
            <Tab
              value="promocodeImports"
              style={{ pointerEvents: 'auto' }}
              label={
                <Tooltip title={promocodeRead ? '' : t('shared.action.permission.required')}>
                  <div>{t('rewards.tabs.promo-codes')}</div>
                </Tooltip>
              }
              icon={<FontAwesomeIcon icon={promoCodeIcon} />}
              disabled={isCreating || !promocodeRead}
            />
          ) : (
            <Tab
              value="codeList"
              style={{ pointerEvents: 'auto' }}
              label={
                <Tooltip title={promocodeRead ? '' : t('shared.action.permission.required')}>
                  <div>
                    {t('rewards.tabs.campaign-code')}
                    {!!errors.data && <FontAwesomeIcon color="red" icon={faExclamationCircle} />}
                  </div>
                </Tooltip>
              }
              icon={<FontAwesomeIcon icon={promoCodeIcon} />}
              disabled={isCreating || !promocodeRead}
            />
          )}
        </Tabs>
      </div>
      <DetailForm.Form>
        <FormProvider {...form}>
          {tab === 'configuration' && <RewardEdition />}
          {tab === 'campaign' && reward.type === RewardType.CAMPAIGN_ACCESS && (
            <CampaignAccessDetails reward={reward} />
          )}
          {tab === 'codeList' && reward.type === RewardType.CAMPAIGN_CODE && (
            <CampaignCodeDetails reward={reward} />
          )}
        </FormProvider>
        {tab === 'promocodeImports' && <PromocodeImportList rewardId={reward.id} />}
      </DetailForm.Form>
      {
        <DetailForm.Footer>
          <Tooltip
            title={
              !rewardWrite
                ? t('shared.action.permission.required')
                : reward.deletedAt
                ? t('rewards.archived.edit.forbidden')
                : ''
            }
            placement="left"
          >
            <div>
              <Button
                startIcon={
                  <FontAwesomeIcon
                    fontSize="inherit"
                    icon={isLoading ? faSpinner : faCheck}
                    spin={isLoading}
                  />
                }
                id={'create-reward-save-button-id'}
                onClick={handleSubmit(onSubmit)}
                color="success"
                disabled={!isDirty || isLoading || !rewardWrite || !!reward.deletedAt}
              >
                {t('rewards.save.button')}
              </Button>
            </div>
          </Tooltip>
        </DetailForm.Footer>
      }
    </DetailForm>
  );
}
